import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import i18next from 'i18next'
import { APIError, authenticatedRequest, getExceptionPayload } from '../axiosConfiguration'
import {
  AssociationsList,
  BusinessPartner,
  ContactsInfo,
  ExtendedContactsInfo,
  UploadDocumentRequest,
  UserAccessInfo,
  UserAssociationsList,
  UserCompanyData,
  UserConfirmRequest,
  UserDocumentInfo,
  UserInviteRequest,
  UserManagementData,
  UserManagementRequest,
  UserPermissions,
  UserProfile,
  USER_TYPE,
} from '../type/users'
import * as layoutActions from './layout'

export const getUsers = createAsyncThunk<UserManagementData, UserManagementRequest, { rejectValue: APIError }>(
  'users/getUsers',
  async (requestPayload, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const res = await instance.post<UserManagementData>('/user/management', requestPayload)

      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUsers')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getUserCompaniesAssociations = createAsyncThunk<UserCompanyData[], USER_TYPE, { rejectValue: APIError }>(
  'users/getUserCompaniesAssociations',
  async (type, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('type', type)

      const res = await instance.get<UserCompanyData[]>(`/user/association?${queryParams.toString()}`)

      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUserCompaniesAssociations')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getContactsSuggestions = createAsyncThunk<ContactsInfo[], string, { rejectValue: APIError }>(
  'users/getContactsSuggestions',
  async (query, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('query', query)

      const res = await instance.get<ContactsInfo[]>(`/contact?${queryParams.toString()}`)

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getContactsSuggestions')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getUserContacts = createAsyncThunk<ExtendedContactsInfo[], string, { rejectValue: APIError }>(
  'users/getUserContacts',
  async (query, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('email', query)

      const res = await instance.get<ExtendedContactsInfo[]>(`/contact?${queryParams.toString()}`)

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUserContacts')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const sendUserInvite = createAsyncThunk<void, UserInviteRequest, { rejectValue: APIError }>(
  'users/sendUserInvite',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()

      await instance.post(`/user/invite`, payload)
      dispatch(
        layoutActions.showToast({
          summary: i18next.t('userManagement:invite.notification.success'),
          severity: 'success',
        }),
      )
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.sendUserInvite')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
      return rejectWithValue(err)
    }
  },
)

export const clearUserInvite = createAction<void>('users/clearUserInvite')

export const deleteUser = createAsyncThunk<void, string, { rejectValue: APIError }>(
  'users/deleteUser',
  async (email, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()

      const queryParams = new URLSearchParams()
      queryParams.append('email', email)

      await instance.delete(`/user/delete?${queryParams.toString()}`)

      dispatch(layoutActions.endLoading())
      dispatch(
        layoutActions.showToast({
          summary: i18next.t('userManagement:delete.notification.success'),
          severity: 'success',
        }),
      )
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.deleteUser')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
      return rejectWithValue(err)
    }
  },
)

export const getUserInfo = createAsyncThunk<UserProfile, string, { rejectValue: APIError }>(
  'users/getUserInfo',
  async (query, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())
    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('email', query)

      const res = await instance.get<UserProfile>(`/user/profile?${queryParams.toString()}`)
      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUserInfo')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getUserDocuments = createAsyncThunk<UserDocumentInfo[], string, { rejectValue: APIError }>(
  'users/getUserDocuments',
  async (query, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('email', query)

      const res = await instance.get<{ data: UserDocumentInfo[] }>(`/user/document?${queryParams.toString()}`)

      return res.data.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUserDocuments')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const uploadUserDocument = createAsyncThunk<void, UploadDocumentRequest, { rejectValue: APIError }>(
  'users/uploadUserDocument',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()

      const { data: signedUrlInfo } = await instance.post<{ url: string; filename: string }>('/user/document/upload', {
        ...payload,
        file: {
          size: payload.file.size,
          name: payload.file.name,
        },
      })

      const formData = new FormData()
      formData.append('multer_file_name', payload.file)
      formData.append('Content-Type', payload.file.type)

      await new Promise((resolve, reject) =>
        axios
          .put(signedUrlInfo.url, payload.file)
          .then(resolve)
          .catch((reason) => {
            dispatch(deleteUserDocument({ filename: signedUrlInfo.filename, implicit: true }))
            reject(reason)
          }),
      )

      dispatch(
        layoutActions.showToast({
          summary: i18next.t('userManagement:upload.notification.success'),
          severity: 'success',
        }),
      )
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.uploadUserDocument')
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getUserDocument = createAsyncThunk<string, string, { rejectValue: APIError }>(
  'users/getUserDocument',
  async (query, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('fileName', query)

      const res = await instance.get<{ url: string }>(`/user/document/file?${queryParams.toString()}`)

      return res.data.url
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUserDocument')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const deleteUserDocument = createAsyncThunk<
  void,
  { filename: string; implicit?: boolean },
  { rejectValue: APIError }
>('users/deleteUserDocument', async ({ filename, implicit }, { dispatch, rejectWithValue }) => {
  dispatch(layoutActions.startLoading())

  try {
    const instance = await authenticatedRequest()

    const queryParams = new URLSearchParams()
    queryParams.append('fileName', filename)

    await instance.delete(`/user/document/file?${queryParams.toString()}`)

    dispatch(layoutActions.endLoading())
    if (!implicit) {
      dispatch(
        layoutActions.showToast({
          summary: i18next.t('userManagement:delete.document.notification.success'),
          severity: 'success',
        }),
      )
    }
  } catch (error) {
    const err = getExceptionPayload(error, 'userManagement:error.deleteUserDocument')

    dispatch(layoutActions.endLoading())
    if (!implicit) {
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
    }
    return rejectWithValue(err)
  }
})

export const getUserAccessRequest = createAsyncThunk<UserAccessInfo, string, { rejectValue: APIError }>(
  'users/getUserAccessRequest',
  async (query, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('email', query)

      const res = await instance.get<UserAccessInfo>(`/user/access-request?${queryParams.toString()}`)
      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUserAccessRequest')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getBusinessAssociations = createAsyncThunk<AssociationsList, string, { rejectValue: APIError }>(
  'users/getBusinessAssociations',
  async (query, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('email', query)

      const res = await instance.get<AssociationsList>(`/association/info?${queryParams.toString()}`)

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getBusinessAssociations')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getUserAssociations = createAsyncThunk<UserAssociationsList, string, { rejectValue: APIError }>(
  'users/getUserAssociations',
  async (query, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('email', query)

      const res = await instance.get<UserAssociationsList>(`/association?${queryParams.toString()}`)
      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUserAssociations')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getUserPermissions = createAsyncThunk<UserPermissions, string, { rejectValue: APIError }>(
  'users/getUserPermissions',
  async (query, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append('email', query)

      const res = await instance.get<UserPermissions>(`/user/authorization?${queryParams.toString()}`)
      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getUserPermissions')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getBusinessPartners = createAsyncThunk<BusinessPartner[], string | number, { rejectValue: APIError }>(
  'users/getBusinessPartners',
  async (query, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      queryParams.append(typeof query === 'number' ? 'id' : 'query', query.toString())

      const res = await instance.get<BusinessPartner[]>(`/businesspartner?${queryParams.toString()}`)

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getBusinessPartners')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const confirmUserRegistration = createAsyncThunk<void, UserConfirmRequest, { rejectValue: APIError }>(
  'users/confirmUserRegistration',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()

      await instance.post(`/user/register/manage`, payload)
      dispatch(
        layoutActions.showToast({
          summary: i18next.t('userManagement:confirm.notification.success'),
          severity: 'success',
        }),
      )
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.confirmUserRegistration')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
      return rejectWithValue(err)
    }
  },
)

export const updateUser = createAsyncThunk<void, UserConfirmRequest, { rejectValue: APIError }>(
  'users/updateUser',
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())
    try {
      const instance = await authenticatedRequest()

      await instance.put(`/user/update`, payload)
      dispatch(layoutActions.endLoading())
      dispatch(
        layoutActions.showToast({
          summary: i18next.t('userManagement:update.notification.success'),
          severity: 'success',
        }),
      )
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.updateUser')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)
